import { isVue2, h as h$1, defineComponent } from 'vue-demi';
import getStroke from 'perfect-freehand';

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const adaptOnsV3 = (ons) => {
    if (!ons)
        return null;
    return Object.entries(ons).reduce((ret, [key, handler]) => {
        key = key.charAt(0).toUpperCase() + key.slice(1);
        key = `on${key}`;
        return Object.assign(Object.assign({}, ret), { [key]: handler });
    }, {});
};

const h = (type, options = {}, chidren) => {
    if (isVue2)
        return h$1(type, options, chidren);
    const { props, domProps, on } = options, extraOptions = __rest(options, ["props", "domProps", "on"]);
    let ons = adaptOnsV3(on);
    const params = Object.assign(Object.assign(Object.assign(Object.assign({}, extraOptions), props), domProps), ons);
    return h$1(type, params, chidren);
};

function getSvgPathFromStroke(stroke) {
    if (!stroke.length)
        return "";
    const d = stroke.reduce((acc, [x0, y0], i, arr) => {
        const [x1, y1] = arr[(i + 1) % arr.length];
        acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
        return acc;
    }, ["M", ...stroke[0], "Q"]);
    d.push("Z");
    return d.join(" ");
}

const DEFAULT_BACKGROUND_COLOR = 'rgba(0,0,0,0)';
const DEFAULT_PEN_COLOR = '#000';
const DEFAULT_WIDTH = '100%';
const DEFAULT_HEIGHT = '100%';
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

var VPerfectSignature = defineComponent({
    data: () => ({
        allInputPoints: [],
        allInputPointsSvgCache: [],
        currentInputPoints: null,
        isDrawing: false,
        cachedImages: [],
        isCaching: false,
        bkDiv: null,
        bkImg: null,
        bkImgOriginalHeight: 0,
        canvasCopy: null,
    }),
    emits: ['onBegin', 'onEnd'],
    props: {
        width: {
            type: String,
            required: false,
            default: DEFAULT_WIDTH
        },
        height: {
            type: String,
            required: false,
            default: DEFAULT_HEIGHT
        },
        backgroundUrl: {
            type: String,
            required: false,
        },
        backgroundColor: {
            type: String,
            required: false,
            default: DEFAULT_BACKGROUND_COLOR
        },
        penColor: {
            type: String,
            required: false,
            default: DEFAULT_PEN_COLOR
        },
        strokeOptions: {
            type: Object,
            required: false,
            default: () => ({})
        },
        cacheSize: {
            type: Number,
            required: false,
            default: 5,
        },
        mode: {
            type: String,
            required: false,
            default: 'draw',
            validator: function (value) {
                // The value must match one of these strings
                return ['draw', 'move', 'disable'].includes(value)
            }
        },
        cssClass: {
            type: String,
            required: false,
            default: '',
        },
    },
    mounted() {
        this.setupBackground();
        this.resizeCanvas();
        this.clear();
        this.setBackgroundAndPenColor();

        window.addEventListener('resize', this.handleResize);
    },
    unmounted() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        hasChanges() {
            return this.allInputPoints.length > 0;
        },
        handlePointerDown(e) {
            if (this.mode !== 'draw') return;
            e.preventDefault();
            const canvas = e.composedPath()[0];
            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            this.currentInputPoints = [[x, y, e.pressure, this.strokeOptions.size]];
            this.isDrawing = true;
            this.$emit('onBegin', e);
        },
        handlePointerMove(e) {
            if (this.mode !== 'draw') return;
            var _a;
            if (!this.isDrawing)
                return;
            if (e.buttons === 1) {
                e.preventDefault();
                const canvas = e.composedPath()[0];
                const rect = canvas.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;

                let point = [x, y, e.pressure, this.strokeOptions.size];
                if (point !== this.currentInputPoints[this.currentInputPoints.length - 1])
                    this.currentInputPoints = [...(_a = this.currentInputPoints) !== null && _a !== void 0 ? _a : [], point];
            }
        },
        handlePointerUp(e) {
            if (this.mode !== 'draw') return;
            e.preventDefault();
            this.isDrawing = false;
            if (!this.currentInputPoints)
                return;
            this.allInputPoints = [...this.allInputPoints, this.currentInputPoints];
            this.currentInputPoints = null;
            this.$emit('onEnd', e);
        },
        handlePointerEnter(e) {
            if (this.mode !== 'draw') return;
            if (e.buttons === 1) {
                this.handlePointerDown(e);
            }
        },
        handlePointerLeave(e) {
            if (this.mode !== 'draw') return;
            if (!this.isDrawing)
                return;
            this.handlePointerUp(e);
        },
        isEmpty() {
            return !this.allInputPoints.length && !this.cachedImages.length;
        },
        clear(full=true) {
            if (full)
                this.cachedImages = [];

            this.allInputPoints = [];
            this.allInputPointsSvgCache = [];
            this.currentInputPoints = null;
        },
        fromData(data) {
            this.allInputPoints = [...this.allInputPoints, ...data];
        },
        toData() {
            return this.allInputPoints;
        },
        fromDataURL(data) {
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => {
                    const canvas = this.getCanvasElement();
                    const rect = canvas.getBoundingClientRect();
                    const ctx = canvas.getContext('2d');

                    ctx === null || ctx === void 0 ? void 0 : ctx.drawImage(image, 0, 0, image.width, image.height);
                    this.cachedImages.push(image);
                    resolve(true);
                };
                image.onerror = () => {
                    reject('Incorrect data uri provided');
                };
                image.crossOrigin = 'anonymous';
                image.src = data;
            });
        },
        toDataURL(type) {
            if (type && !IMAGE_TYPES.includes(type))
                throw new Error(`Incorrect image type. Must be one of ${IMAGE_TYPES.join(', ')}.`);

            if (this.isEmpty())
                return;

            let canvas = this.getCanvasElement();
            let dataURL = canvas.toDataURL(type !== null && type !== void 0 ? type : 'image/png');

            return dataURL;
        },
        getCanvasElement() {
            return this.$refs.signaturePad;
        },
        setBackgroundAndPenColor() {
            const canvas = this.getCanvasElement();
            const rect = canvas.getBoundingClientRect();
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = this.backgroundColor;
            ctx === null || ctx === void 0 ? void 0 : ctx.fillRect(0, 0, rect.width, rect.height);
            ctx.fillStyle = this.penColor;
        },
        setBackgroundUrl(url, clear=false) {
            if (typeof url === "undefined" || url === null)
                url = this.backgroundUrl;

            if (typeof url === "undefined" || url === null)
                return;

            this.backgroundUrl = url;
            let buster = url + '?t=' + (new Date()).getTime();

            // Preload to avoid flash when changing images
            let image = new Image();
            image.src = buster;
            image.onload = () => {
                this.getCanvasElement().style.backgroundImage = 'url("' + buster + '")';
                if (clear)
                    this.clear();
            }
        },
        setupBackground() {
            const canvas = this.getCanvasElement();
            let scale = visualViewport.width / this.width;

            canvas.width = visualViewport.width;
            canvas.height = this.height * scale;
            canvas.style.backgroundRepeat = 'no-repeat';
            canvas.style.backgroundSize = '100%';

            this.setBackgroundUrl();
        },
        resizeCanvas() {
            const canvas = this.getCanvasElement();
            const rect = canvas.getBoundingClientRect();

            const dpr = window.devicePixelRatio || 1;

            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;

            const ctx = canvas.getContext('2d');
            ctx === null || ctx === void 0 ? void 0 : ctx.scale(dpr, dpr);

            canvas.style.width = rect.width + 'px';
            canvas.style.height = rect.height + 'px';

            this.inputPointsHandler();
        },
        drawAllInputPoints(ctx, scale=1) {
            this.allInputPoints.forEach((point, index) => {
                let svg;

                // Bypass cache for scale changes
                if (scale !== 1) {
                    let strokeOptions = JSON.parse(JSON.stringify(this.strokeOptions));
                    strokeOptions.size = point[0][3]*scale;
                    let resizePoint = point.map((arr) => { return [arr[0]*scale, arr[1]*scale, arr[2], arr[3]]; });
                    svg = getSvgPathFromStroke(getStroke(resizePoint, strokeOptions));
                }

                else {
                    // Cache if required
                    if (typeof this.allInputPointsSvgCache[index] === "undefined")
                        this.allInputPointsSvgCache[index] = getSvgPathFromStroke(getStroke(point, this.strokeOptions));

                    svg = this.allInputPointsSvgCache[index];
                }

                const myPath = new Path2D(svg);
                ctx === null || ctx === void 0 ? void 0 : ctx.fill(myPath);
            });
        },
        inputPointsHandler() {
            const canvas = this.getCanvasElement();
            const rect = canvas.getBoundingClientRect();

            const ctx = canvas.getContext('2d');
            ctx === null || ctx === void 0 ? void 0 : ctx.clearRect(0, 0, rect.width, rect.height);
            this.setBackgroundAndPenColor();

            this.cachedImages.forEach((image) => ctx === null || ctx === void 0 ? void 0 : ctx.drawImage(image, 0, 0, image.width, image.height));

            this.drawAllInputPoints(ctx);

            if (! this.currentInputPoints)
                return;

            const pathData = getSvgPathFromStroke(getStroke(this.currentInputPoints, this.strokeOptions));
            const myPath = new Path2D(pathData);
            ctx === null || ctx === void 0 ? void 0 : ctx.fill(myPath);
        },
        undo() {
            this.allInputPoints.pop();
            this.allInputPointsSvgCache.pop();
        },
        touchEvent(e) {
            if (this.mode !== 'draw') return;

            if (e.touches.length > 1)
                e.touches = e.touches[0];

            e.preventDefault();
        },
        handleResize(e) {
            this.resizeCanvas();
        },
        async save() {
            let canvas = this.getCanvasElement();
            let dpr = window.devicePixelRatio || 1;

            // Create full size canvas
            let canvasCopy = document.createElement("canvas");
            canvasCopy.width = parseFloat(this.width);
            canvasCopy.height = parseFloat(this.height);

            // Re-draw all points in full-size
            let canvasCopyCtx = canvasCopy.getContext("2d");
            let scale = this.width / canvas.width;
            this.drawAllInputPoints(canvasCopyCtx, scale*dpr);

            // Find x,y where scribbles have been made
            let imageData = canvasCopyCtx.getImageData(0, 0, canvasCopy.width, canvasCopy.height);
            let top = 0, bottom = imageData.height, left = 0, right = imageData.width;

            //while (top < bottom && this.rowBlank(imageData, width, top)) ++top;
            while (bottom - 1 > top && this.rowBlank(imageData, canvasCopy.width, bottom - 1)) --bottom;
            //while (left < right && this.columnBlank(imageData, width, left, top, bottom)) ++left;
            while (right - 1 > left && this.columnBlank(imageData, canvasCopy.width, right - 1, top, bottom)) --right;

            // Trim this region and dump into new canvas
            let trimmedImageData = canvasCopyCtx.getImageData(left, top, right - left, bottom - top);
            let trimmed = document.createElement("canvas");
            let trimmedCtx = trimmed.getContext("2d");
            trimmed.width = trimmedImageData.width;
            trimmed.height = trimmedImageData.height;

            trimmedCtx.putImageData(trimmedImageData, 0, 0);

            return trimmed.toDataURL();
        },
        rowBlank(imageData, width, y) {
            for (var x = 0; x < width; ++x)
                if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;

            return true;
        },
        columnBlank(imageData, width, x, top, bottom) {
            for (var y = top; y < bottom; ++y)
                if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;

            return true;
        },
    },
    watch: {
        backgroundColor() {
            this.setBackgroundAndPenColor();
        },
        penColor(color) {
            const canvas = this.getCanvasElement();
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = color;
        },
        allInputPoints: {
            deep: true,
            handler() {
                this.inputPointsHandler();
            }
        },
        currentInputPoints: {
            deep: true,
            handler() {
                this.inputPointsHandler();
            }
        },
    },
    render() {
        const { width, height, mode } = this;
        const handlers = {
            pointerdown: this.handlePointerDown,
            pointermove: this.handlePointerMove,
            pointerup: this.handlePointerUp,
            pointerleave: this.handlePointerLeave,
            pointerenter: this.handlePointerEnter,
            pointercancel: this.handlePointerUp,

            mousedown: this.handlePointerDown,
            mousemove: this.handlePointerMove,
            mouseup: this.handlePointerUp,
            mouseleave: this.handlePointerLeave,

            touchstart: this.touchEvent,
            touchmove: this.touchEvent,
            touchend: this.touchEvent,
            touchleave: this.touchEvent,
            touchcancel: this.touchEvent,
        };
        return h('canvas', {
            ref: 'signaturePad',
            style: {
                height,
                width,
                touchAction: mode === 'move' ? 'auto' : 'none',
            },
            class: this.cssClass,
            on: handlers
        });
    }
});

export default VPerfectSignature;
//# sourceMappingURL=v-perfect-signature.esm.js.map
